import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Evaluator scores are stored on a 1 to 5 scale. Shown to users as a
 * percentage where 1 is 0% and 5 is 100%, so a complete failure reads as
 * a failure rather than as partial credit.
 */
export function scoreToPercentage(score: number) {
  const clamped = Math.min(5, Math.max(1, score));
  return ((clamped - 1) / 4) * 100;
}

export function getRatingColor(score: number) {
  const percentage = scoreToPercentage(score);
  // Same colour for each score as before: 4 and above green, 3 yellow,
  // 2 orange, 1 red.
  if (percentage >= 75) return 'bg-green-100 text-green-800';
  if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
  if (percentage >= 25) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
}

export function formatRating(score: number) {
  return `${Math.round(scoreToPercentage(score))}%`;
}

export function isNotApplicableScore(score: number | string | undefined) {
  return score === 'N/A' || score === 'n/a';
}

export function isPerfectEvaluationScore(score: number | string | undefined) {
  return !isNotApplicableScore(score) && score === 5;
}

/** @deprecated Use isNotApplicableScore */
export const isNotTestedScore = isNotApplicableScore;

export function getEvaluationScoreColor(score: number | string) {
  if (isNotApplicableScore(score)) return 'bg-gray-100 text-gray-800';
  return getRatingColor(score as number);
}

export function formatEvaluationScore(score: number | string) {
  if (isNotApplicableScore(score)) return 'Not applicable';
  return formatRating(score as number);
}

export function normalizeKey(key: string) {
  return key
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function truncateString({
  str,
  maxLength = 20,
}: {
  str: string;
  maxLength: number;
}): string {
  if (!str || str.length <= maxLength || maxLength <= 0) {
    return str;
  }
  return str.slice(0, maxLength - 3) + '...';
}

export function replaceValueWithEmpty<T extends Record<string, unknown>>(
  obj: T,
  valueToReplace: string,
  replacement: string = '',
): T {
  const entries = Object.entries(obj).map(([key, value]) => [
    key,
    value === valueToReplace ? replacement : value,
  ]);
  return Object.fromEntries(entries) as T;
}

/** Hover text for a score that could not be given. */
export const NOT_APPLICABLE_EXPLANATION =
  'Nothing in this conversation was relevant to this evaluator, so it could not be scored.';

export function getEvaluationScoreTitle(score: number | string) {
  return isNotApplicableScore(score) ? NOT_APPLICABLE_EXPLANATION : undefined;
}
